<template>
  <CContainer>
    <CRow class="justify-content-center">
      <CCol md="12" class="text-start">
        <CRow class="tardy-module-header-row flex-wrap gy-2">
          <CCol class="d-flex min-w-0 col-12 col-md-6">
            <div class="v3-module-list-title" role="heading" aria-level="2">
              Tardy Management
            </div>
            <HelpCenterButton
              classes="ms-2"
              content-key="module_tardy_management"
            />
          </CCol>
          <CCol
            class="d-flex flex-row-reverse align-items-center col-12 col-md-6"
          >
            <CustomSwitch
              type="radio"
              aria-label="Tardy Management, toggle module on or off"
              :model-value="state.isActive"
              @change="toggleTardy(state.isActive)"
            ></CustomSwitch>
          </CCol>
        </CRow>
        <div class="v3-module-list-box" v-if="state.isActive">
          <div>
            <p class="fw-bold mb-2" role="heading" aria-level="3">
              Tardy Input Method Options:
            </p>

            <div class="ms-3 mb-4">
              <p class="mb-2">Input by file</p>
              <BaseButton
                class="mb-2"
                data-testid="tardy-csv-setup-button"
                @click="goToTardyCsvUploadPage"
                rounded
                >CSV Setup</BaseButton
              >
              <p class="mb-0 small text-muted">
                Files can be manually uploaded or automated via SFTP. Click "CSV
                Setup" for details.
              </p>
            </div>

            <p class="mb-3">Adult Initiated (Teacher Pass)</p>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                :model-value="isAdminOnly"
                label="Allow admin only"
                @change="(value) => selectAdultScope('admin', value)"
              ></CustomCheck>
            </div>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                :model-value="isAllAdultUsers"
                label="Allow all adult users"
                @change="(value) => selectAdultScope('all', value)"
              ></CustomCheck>
            </div>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                :model-value="isSpecificStaff"
                label="Only allow specific teachers/staff"
                @change="(value) => selectAdultScope('specific', value)"
              ></CustomCheck>
            </div>
            <BaseButton
              v-if="isSpecificStaff"
              class="mb-4"
              @click="goToSelectAdultsPage"
              rounded
              >Add Adults</BaseButton
            >

            <p class="mb-3">Student Initiated (Kiosk and Student Late Pass)</p>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                v-model="state.form.allowStudentsLTS"
                label="Mark Late to School (LTS)"
                @change="allowStudentsLTS"
              ></CustomCheck>
            </div>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                v-model="state.form.allowStudentsLTC"
                label="Mark Late to Class (LTC)"
                @change="allowStudentsLTC"
              ></CustomCheck>
            </div>

            <p class="fw-bold mb-2" role="heading" aria-level="3">
              Student View Options:
            </p>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                v-model="state.form.allowStudentsSeeLevel"
                label="Allow students to see Ladder"
                @change="submit"
              ></CustomCheck>
            </div>

            <div
              class="d-flex tardy-module-student-options flex-wrap align-items-start mt-3"
            >
              <p
                class="col d-flex min-w-0 col-12 col-md-6 text-start align-items-center mb-0"
              >
                Count SLP as a pass against a student pass limit
              </p>
              <div
                class="col d-flex flex-row-reverse align-items-center col-12 col-md-6"
              >
                <CustomSwitch
                  aria-label="Count SLP as a pass against a student pass limit"
                  :model-value="state.slpCount"
                  @change="toggleSLPCount"
                ></CustomSwitch>
              </div>
            </div>
          </div>
        </div>
        <CAlert
          v-if="state.reqResponse.message"
          dismissible
          class="mt-4"
          :color="state.reqResponse.type"
          :show="!!state.reqResponse.message"
          role="status"
          aria-live="polite"
          @close="resetResponseMessages"
        >
          {{ state.reqResponse.message }}
        </CAlert>
      </CCol>
    </CRow>
  </CContainer>
</template>
<script>
import HelpCenterButton from "../../HelpCenterButton.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import CustomCheck from "@/v3components/shared/Form/CustomCheck.vue"
import CustomSwitch from "@/v3components/shared/Form/CustomSwitch.vue"
import { useStore } from "vuex"
import { onMounted, computed, reactive, ref } from "vue"
import { useRouter } from "vue-router"
import passConstants from "@/constants/passConstants"

export default {
  name: "TardyManagementModuleForm",
  components: {
    HelpCenterButton,
    BaseButton,
    CustomCheck,
    CustomSwitch
  },
  setup() {
    const store = useStore()

    const router = useRouter()

    const state = reactive({
      form: {
        allowTeacherStaff: false,
        allowSelectedUsersOnly: false,
        allowStudentsLTC: false,
        allowStudentsLTS: false,
        allowStudentsSeeLevel: true,
        usageCSV: false,
        usageEHP: false,
        fileUploadMode: passConstants.DEFAULT_FILE_UPLOAD_MODE_FOR_TARDY
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      isActive: false,
      slpCount: false
    })

    const isLoading = ref(false)

    const schoolDomains = computed(() => store.getters["schools/schoolDomains"])
    const currentUser = computed(() => store.getters["authentication/user"])

    const options = computed(() => {
      const tardyModule = store.state.schools.activeSchoolModules.find(
        (module) => module.name === "Tardy Management"
      )
      return tardyModule ? JSON.parse(tardyModule.pivot.option_json) : false
    })

    const active = computed(() => {
      const tardyModule = store.state.schools.activeSchoolModules.find(
        (module) => module.name === "Tardy Management"
      )
      return tardyModule ? Boolean(tardyModule.pivot.status) : false
    })

    const isAdminOnly = computed(
      () =>
        state.form.usageEHP &&
        !state.form.allowTeacherStaff &&
        !state.form.allowSelectedUsersOnly
    )

    const isAllAdultUsers = computed(
      () =>
        state.form.usageEHP &&
        state.form.allowTeacherStaff &&
        !state.form.allowSelectedUsersOnly
    )

    const isSpecificStaff = computed(
      () => state.form.usageEHP && state.form.allowSelectedUsersOnly
    )

    onMounted(() => {
      if (options.value) {
        setForm()
      }
      state.isActive = active.value
    })

    const setForm = () => {
      state.form.allowTeacherStaff = options.value["allow-teacher-staff"]
      state.form.allowSelectedUsersOnly =
        options.value["allow-selected-users-only"]
      state.form.allowStudentsLTS =
        options.value["allow-students-create"]["lts"]
      state.form.allowStudentsLTC =
        options.value["allow-students-create"]["ltc"]
      state.form.allowStudentsSeeLevel =
        options.value["allow-students-see-level"] ?? true
      state.form.usageCSV = options.value.usage["via-csv"]
      state.form.usageEHP = options.value.usage["within-ehp"]
      state.form.fileUploadMode =
        options?.value?.["file-upload-mode"] ||
        passConstants.DEFAULT_FILE_UPLOAD_MODE_FOR_TARDY
      state.slpCount = Boolean(options.value?.["count-slp-against-pass-limit"])
    }

    const selectAdultScope = (scope, value) => {
      if (value) {
        state.form.usageEHP = true
        state.form.allowTeacherStaff = scope === "all"
        state.form.allowSelectedUsersOnly = scope === "specific"
      } else {
        state.form.usageEHP = false
        state.form.allowTeacherStaff = false
        state.form.allowSelectedUsersOnly = false
      }
      submit()
    }

    const allowStudentsLTC = (value) => {
      state.form.allowStudentsLTC = value
      submit()
    }

    const allowStudentsLTS = (value) => {
      state.form.allowStudentsLTS = value
      submit()
    }

    const submit = () => {
      setTimeout(() => {
        resetResponseMessages()
        const inputsData = JSON.stringify({
          "allow-teacher-staff": state.form.allowTeacherStaff,
          "allow-selected-users-only": state.form.allowSelectedUsersOnly,
          "allow-students-create": {
            ltc: state.form.allowStudentsLTC,
            lts: state.form.allowStudentsLTS
          },
          "allow-students-see-level": state.form.allowStudentsSeeLevel,
          "file-upload-mode": state.form.fileUploadMode,
          "count-slp-against-pass-limit": state.slpCount,
          usage: {
            "via-csv": state.form.usageCSV,
            "within-ehp": state.form.usageEHP
          }
        })
        isLoading.value = true
        store
          .dispatch("modules/updateTardyModule", { option_json: inputsData })
          .then(() => {
            setSuccessResponse("Module updated.")
            store
              .dispatch("schools/getActiveModules", {
                clearCacheEntry: true
              })
              .then(() => {
                setForm()
              })
            isLoading.value = false
          })
          .catch((err) => {
            const response = err.response.data
            setErrorResponse(response.message, response.errors)
            isLoading.value = false
            store
              .dispatch("schools/getActiveModules", {
                clearCacheEntry: true
              })
              .then(() => {
                setForm()
              })
          })
      }, 100)
    }

    const toggleTardy = (status) => {
      store
        .dispatch("modules/toggleModule", {
          id: 5,
          moduleStatus: { status: status ? 0 : 1 }
        })
        .then(() => {
          state.isActive = !state.isActive
          store
            .dispatch("schools/getActiveModules", {
              clearCacheEntry: true
            })
            .then(() => {
              getExceededIncidents()
            })
        })
    }

    const toggleSLPCount = (value) => {
      state.slpCount = Boolean(value)
      submit()
    }

    const getExceededIncidents = () => {
      state.isActive
        ? store.dispatch("exceededIncidents/getExceededIncidents")
        : store.commit("exceededIncidents/SET_EXCEEDED_INCIDENTS", false)
    }

    const goToSelectAdultsPage = () => {
      router.push("/tardy-management/adults")
    }

    const goToTardyCsvUploadPage = () => {
      router.push("/tardy-management/tardy-csv-upload")
    }

    const resetResponseMessages = () => {
      state.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    }

    const setSuccessResponse = (message) => {
      state.reqResponse = {
        type: "success",
        message: message ? message : "Success!",
        errors: {}
      }
    }

    const setErrorResponse = (message, errors) => {
      state.reqResponse = {
        type: "danger",
        message: message ? message : "Something went wrong!",
        errors: errors ? errors : {}
      }
    }
    return {
      state,
      schoolDomains,
      currentUser,
      isAdminOnly,
      isAllAdultUsers,
      isSpecificStaff,
      setForm,
      selectAdultScope,
      allowStudentsLTC,
      allowStudentsLTS,
      submit,
      toggleTardy,
      goToSelectAdultsPage,
      goToTardyCsvUploadPage,
      resetResponseMessages,
      toggleSLPCount
    }
  }
}
</script>
